// Shared helpers for the Event Highlights feature. Lives here so
// EventHighlights.vue stays focused on tab orchestration and the dialog
// components can import the same map/format functions.
//
// Cost (`doc.cost`) and expiry (`doc.expiresAt`) come from the backend —
// see EventHighlightsController + UserHighlight/UserHighlightPurchase
// accessors — so this module no longer hardcodes either.

// Laravel LengthAwarePaginator helpers — keep the snake_case field names
// contained here instead of leaking them into every call site.
export function pageItems(page) {
  return Array.isArray(page?.data) ? page.data : [];
}
export function pageHasMore(page) {
  return (page?.current_page ?? 1) < (page?.last_page ?? 1);
}
export function pageCurrent(page) {
  return page?.current_page ?? 1;
}

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function parseDate(value) {
  if (value == null || value === "") return null;
  if (value instanceof Date) return isNaN(value) ? null : value;
  // MongoDB extended JSON: { $date: "..." } or { $date: { $numberLong: "..." } }
  if (typeof value === "object") {
    const inner = value.$date;
    if (inner && typeof inner === "object" && inner.$numberLong != null) {
      const d = new Date(Number(inner.$numberLong));
      return isNaN(d) ? null : d;
    }
    if (typeof inner === "string" || typeof inner === "number") {
      const d = new Date(inner);
      return isNaN(d) ? null : d;
    }
    return null;
  }
  const d = new Date(value);
  return isNaN(d) ? null : d;
}

export function formatDate(value) {
  const d = parseDate(value);
  if (!d) return "";
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${day},${MONTHS[d.getUTCMonth()]},${d.getUTCFullYear()}`;
}

export function formatBytes(bytes) {
  if (!bytes || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let n = bytes;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n < 10 ? n.toFixed(2) : n.toFixed(1)} ${units[i]}`;
}

// Shared between mapHighlight and mapPurchase — both items already include
// pre-signed S3 URLs from the backend (`watermarkUrl` for the player,
// `finalUrl` for the download button), so the frontend just reads them.
function videoFields(doc) {
  return {
    title: doc.eventTitle || doc.eventId || "Highlight",
    thumbnail: "",
    videoUrl: doc.watermarkUrl || "",
    downloadUrl: doc.finalUrl || "#",
  };
}

// Map a user_highlights document (from /whoosh/events/highlights) to the
// shape HighlightCard / HighlightVideoPlayer expects.
export function mapHighlight(doc) {
  return {
    ...videoFields(doc),
    id: doc._id,
    highlightId: doc._id,
    purchaseId: null,
    points: Number(doc.cost) || 0,
    eventDate: formatDate(doc.createdAt),
    expiryDate: formatDate(doc.expiresAt),
  };
}

// Map a user_highlight_purchases row to the same card shape. `id` is the
// purchase doc id (so we can DELETE it server-side), `highlightId` keeps the
// original highlight reference, and expiry comes straight from the server.
export function mapPurchase(doc) {
  return {
    ...videoFields(doc),
    id: doc._id,
    highlightId: doc.highlightId,
    purchaseId: doc._id,
    points: Number(doc.gemsSpent) || 0,
    eventDate: formatDate(doc.purchasedAt),
    expiryDate: formatDate(doc.expiresAt),
  };
}
